import GapFillIcon from "@/components/icons/exerciseV2/GapFillIcon";
import { Button } from "@/components/ui/Button";
import { useModalAction } from "@/contexts/modal/Context";
import { FC, useEffect, useState } from "react";

export const characterSpecialForSplit = "%&%&%";

export const checkCharacterSpecialForSplit = (word: string) => {
  let newWord = word;

  [".", ",", "-", "!", "?"].forEach(
    (s) => (newWord = newWord.trim().replaceAll(s, "")),
  );

  return newWord === characterSpecialForSplit;
};

interface QuestionProgressBarProps {
  totalQuestions: number;
  currentQuestion: number;
}

interface QuestionProps {
  answers: Answers;
  question: string;
  onAnswer: (index: number, answer: string) => void;
  showCorrection?: boolean;
  correctionData?: {
    [key: number]: {
      isCorrect: boolean;
      studentAnswer: string;
      correctAnswer: string;
    };
  };
  currentQuestionIndex: number;
}

interface CorrectionFeedbackProps {
  question: string;
  correctionData: {
    [key: number]: {
      isCorrect: boolean;
      studentAnswer: string;
      correctAnswer: string;
    };
  };
  currentQuestionIndex: number;
}

export interface QuestionData {
  id: number;
  statement: string;
  correct_answer: string;
  questionWithBlanks?: string;
  title?: string;
}

interface GapFillExerciseProps {
  questions: QuestionData[];
}

interface CorrectionData {
  isCorrect: boolean;
  correctAnswer: string;
  studentAnswer: string;
}

interface PreparedData {
  [key: number]: {
    questionId: number;
    textAnswer: string;
  }[];
}

interface Answers {
  [key: number]: string;
}

const QuestionProgressBar: FC<QuestionProgressBarProps> = ({
  currentQuestion,
  totalQuestions,
}) => {
  const progressPercentage = ((currentQuestion / totalQuestions) * 100).toFixed(
    0,
  );

  return (
    <div className="flex flex-col self-start mt-2 w-[200px]">
      <div className="text-xs font-semibold leading-3 text-brand-secondary whitespace-nowrap">
        Question {currentQuestion}/{totalQuestions}
      </div>

      <div className="flex flex-col justify-center mt-1.5 rounded-sm bg-zinc-300">
        <div
          className="shrink-0 h-1 bg-brand-secondary rounded-sm"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
    </div>
  );
};

const CorrectionFeedback: FC<CorrectionFeedbackProps> = ({
  question,
  correctionData,
  currentQuestionIndex,
}) => {
  let placeholderCount = 0;

  return (
    <div className="flex gap-1 items-center flex-wrap px-8 py-3 w-full text-brand font-normal bg-[#219653] bg-opacity-20 rounded-md">
      <p className="text-sm font-semibold ">{currentQuestionIndex + 1} .</p>

      {question.split(" ").map((word, index) => {
        if (checkCharacterSpecialForSplit(word)) {
          const correction = correctionData[placeholderCount];
          const correctAnswer = correction?.correctAnswer.split("|");

          placeholderCount++;

          return (
            <span
              key={index}
              className={`rounded min-w-[100px] text-center py-2 text-white ${
                correction && "bg-[#219653]"
              }`}
            >
              {correctAnswer.find((a) => {
                const ca = a.trim().toLowerCase();
                const sa = correction?.studentAnswer.trim().toLowerCase();
                return ca.charAt(0) == sa.charAt(0) && ca.includes(sa);
              }) ??
                correctAnswer[0] ??
                "Errore"}
            </span>
          );
        }

        return (
          <span key={index} className="my-auto">
            {word}
          </span>
        );
      })}
    </div>
  );
};

const Question: FC<QuestionProps> = ({
  answers,
  question,
  onAnswer,
  showCorrection,
  correctionData,
  currentQuestionIndex,
}) => {
  let placeholderIndex = 0;

  return (
    <div className="flex flex-col">
      <div className="flex gap-1 items-center flex-wrap px-8 py-3 w-full bg-brand-light rounded-md text-brand">
        <p className="text-sm font-semibold ">{currentQuestionIndex + 1} .</p>

        {question.split(" ").map((word, index) => {
          if (!checkCharacterSpecialForSplit(word)) {
            return (
              <span key={index} className="my-auto font-normal">
                {word}
              </span>
            );
          }

          const correction =
            correctionData && correctionData[placeholderIndex]
              ? correctionData[placeholderIndex]
              : null;

          placeholderIndex++;

          const isCorrect = correction ? correction.isCorrect : null;

          return (
            <input
              key={index}
              type="text"
              onInput={(e) => {
                const target = e.target as HTMLInputElement;
                target.style.width = `${target.scrollWidth}px`;
              }}
              value={answers[index] ?? ""}
              onChange={(e) => onAnswer(index, e.target.value)}
              readOnly={showCorrection}
              className={`bg-white text-center text-brand ring-1 rounded p-1 w-[100px] ${
                showCorrection
                  ? isCorrect
                    ? "ring-[#219653]"
                    : "ring-[#C80505]"
                  : "ring-brand-secondary"
              }`}
              placeholder="___"
            />
          );
        })}
      </div>
    </div>
  );
};

const GapFillExercise: FC<GapFillExerciseProps> = ({ questions }) => {
  const { openModal } = useModalAction();

  const [answers, setAnswers] = useState<Answers>({});
  const [preparedData, setPreparedData] = useState<PreparedData>({});
  const [showCorrection, setShowCorrection] = useState<boolean>(false);
  const [correctionData, setCorrectionData] = useState<CorrectionData[]>([]);
  const [preparedQuestions, setPreparedQuestions] = useState<QuestionData[]>(
    [],
  );
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);

  const totalQuestions = questions.length;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const currentQuestion = preparedQuestions[currentQuestionIndex];

  const handleNextQuestion = () => {
    setCurrentQuestionIndex(currentQuestionIndex + 1);
    setAnswers({});
    setShowCorrection(false);
  };

  const handleAnswer = (index: number, answer: string) => {
    setAnswers((p) => ({
      ...p,
      [index]: answer,
    }));
  };

  const handleShowCorrection = () => {
    if (!currentQuestion || !currentQuestion.questionWithBlanks) {
      console.error("Current question or question with blanks is undefined.");
      return;
    }

    const blanksIndices: number[] = [];
    currentQuestion.questionWithBlanks.split(" ").forEach((word, index) => {
      if (checkCharacterSpecialForSplit(word)) {
        blanksIndices.push(index);
      }
    });

    const correctAnswersArray = currentQuestion.correct_answer
      .split(":")
      .map((a) => a.trim());
    const correctedAnswers = blanksIndices.map((b, i) => {
      const studentAnswer = answers[b] || "";
      const correctAnswer = correctAnswersArray[i];

      const isCorrect = correctAnswer
        .split("|")
        .some(
          (ca) => ca.trim().toLowerCase() == studentAnswer.trim().toLowerCase(),
        );

      return { studentAnswer, correctAnswer, isCorrect };
    });

    setCorrectionData(correctedAnswers);
    setShowCorrection(true);

    const correctAnswersForCurrentQuestion =
      correctedAnswers.filter((a) => a.isCorrect).length /
      correctedAnswers.length;

    setCorrectAnswersCount((p) => p + correctAnswersForCurrentQuestion);
  };

  const handleShowMyScore = async () => {
    const prepareSubmissionData: { [x: string]: Array<string> } = {};

    Object.values(preparedData).forEach(
      (
        e: Array<{
          questionId: number;
          textAnswer: string;
        }>,
      ) => {
        e.forEach((t) => {
          if (!prepareSubmissionData[t.questionId]) {
            prepareSubmissionData[t.questionId] = [];
          }

          prepareSubmissionData[t.questionId].push(t.textAnswer);
        });
      },
    );

    const submissionData = Object.keys(prepareSubmissionData).map(
      (questionId) => ({
        questionId,
        textAnswer: prepareSubmissionData[questionId].join(":"),
      }),
    );

    // OPEN MODAL WITH NEXT EXERCISE BUTTON
    openModal(
      "EXERCISE_V2_MODAL",
      {
        total: questions?.length,
        score: Number.isInteger(correctAnswersCount)
          ? correctAnswersCount
          : correctAnswersCount.toFixed(2),
        exerciseType: "gap_fill",
        contentClass: "bg-brand-lavender",
        submissionData: submissionData,
      },
      {
        renderHeader: (
          <div className="flex items-center px-2 h-10 bg-brand-secondary rounded-t-md">
            <p className="text-14px text-white font-semibold">Gap fill</p>
          </div>
        ),
      },
    );
  };

  const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };
useEffect(() => {
  const prepareQuestions = questions.map((question) => {
    const keyWords = question.correct_answer
      .split(":")
      .map((answer) => answer.trim());

    let questionWithBlanks = question.statement;

    // Match and temporarily remove all parenthesis blocks like (CHOOSE:...) to avoid replacing text inside them
    const parenBlocks: string[] = [];
    questionWithBlanks = questionWithBlanks.replace(/\([^()]*\)/g, (match) => {
      parenBlocks.push(match);
      return `<<<PLACEHOLDER_${parenBlocks.length - 1}>>>`;
    });

    keyWords.forEach((word) => {
      const regex = new RegExp(`\\b${escapeRegExp(word)}\\b`, "gi");
      questionWithBlanks = questionWithBlanks.replace(regex, characterSpecialForSplit);
    });

    // Restore original parenthesis content
    questionWithBlanks = questionWithBlanks.replace(/<<<PLACEHOLDER_(\d+)>>>/g, (_: string, i: string) => {
    return parenBlocks[parseInt(i, 10)];
    });


    return { ...question, questionWithBlanks };
  });

  setPreparedQuestions(prepareQuestions);
}, [questions]);


  useEffect(() => {
    if (correctionData.length > 0 && currentQuestion) {
      const submissions = correctionData.map((data) => ({
        questionId: currentQuestion.id,
        textAnswer: data.studentAnswer,
      }));

      setPreparedData((p) => ({
        ...p,
        [currentQuestionIndex]: submissions,
      }));
    }
  }, [correctionData, currentQuestion, currentQuestionIndex]);

  useEffect(() => {
    // Blocca lo scroll
    document.body.style.overflowY = "hidden";

    // Riabilita lo scroll quando il componente viene smontato
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, []);

  return (
    <div className="flex flex-col gap-5 px-5 py-5 bg-white rounded-lg border border-brand-secondary border-solid max-md:px-5">
      <header className="flex gap-5 justify-between w-full max-md:flex-wrap max-md:max-w-full">
        <div className="flex gap-5 justify-between text-xl leading-6 text-brand">
          <GapFillIcon className="w-[40px] h-[32px]" />

          <div className="w-full italic">
            <p className="text-sm font-semibold ">Gap fill</p>

            <p className="text-xs font-normal italic text-brand">
              Choose the correct question
            </p>
          </div>
        </div>

        <div className="flex gap-6">
          <QuestionProgressBar
            totalQuestions={totalQuestions}
            currentQuestion={currentQuestionIndex + 1}
          />

          <Button
            size="sm"
            variant="secondary"
            onClick={handleShowCorrection}
            disabled={!Object.keys(answers)?.length || showCorrection}
            className="rounded-[10px] h-[30px] text-[14px]"
          >
            Correct now
          </Button>
        </div>
      </header>
      <div>
        <p className="text-xs font-normal  text-black">
          {currentQuestion?.title ? currentQuestion?.title : ""}
        </p>
      </div>

      <div className="flex flex-col gap-11">
        <section className="flex flex-col gap-2 text-sm leading-4 min-h-[200px]">
          {currentQuestion && (
            <Question
              answers={answers}
              question={currentQuestion?.questionWithBlanks || ""}
              onAnswer={(index, answer) => handleAnswer(index, answer)}
              showCorrection={showCorrection}
              correctionData={correctionData}
              currentQuestionIndex={currentQuestionIndex}
            />
          )}

          {showCorrection && (
            <CorrectionFeedback
              question={currentQuestion?.questionWithBlanks || ""}
              correctionData={correctionData}
              currentQuestionIndex={currentQuestionIndex}
            />
          )}
        </section>

        {showCorrection && (
          <Button
            size="default"
            variant={isLastQuestion ? "default" : "outline"}
            onClick={isLastQuestion ? handleShowMyScore : handleNextQuestion}
            className="mx-auto border-brand text-[16px]"
          >
            {isLastQuestion ? "My Score" : "Next question"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default GapFillExercise;
